import { createFileRoute, redirect } from '@tanstack/react-router';
import * as z from 'zod';

import {
	getGetPolicyPlanVersionQueryOptions,
	getListPolicyPlanVersionsQueryOptions
} from '@praetoid/client';

import { PraetorPolicyDetailPage } from '~/app/praetors/policies/policy/PolicyPlan';
import { queryClient } from '~/lib/queryClient';
import { getLatestVersionId } from '~/lib/versions';

const versionIdSchema = z.uuid();

interface PolicyDetailSearch {
	edit: boolean;
	version?: string;
}

export const Route = createFileRoute('/_app/home/praetors/$praetorId/policies/$policyId')({
	validateSearch: (search: Record<string, unknown>): PolicyDetailSearch => {
		const version =
			typeof search.version === 'string' && versionIdSchema.safeParse(search.version).success
				? search.version
				: undefined;

		return version
			? { edit: search.edit === true || search.edit === 'true', version }
			: { edit: search.edit === true || search.edit === 'true' };
	},
	beforeLoad: async ({ params: { praetorId, policyId }, search: { edit, version } }) => {
		const resolvedVersionId =
			version ??
			getLatestVersionId(
				await queryClient.ensureQueryData(getListPolicyPlanVersionsQueryOptions(policyId))
			);

		if (resolvedVersionId && resolvedVersionId !== version) {
			throw redirect({
				to: '/home/praetors/$praetorId/policies/$policyId',
				params: { praetorId, policyId },
				search: { edit, version: resolvedVersionId },
				replace: true
			});
		}

		if (!resolvedVersionId) {
			return { getTitle: 'Policy' };
		}

		try {
			const versionOptions = getGetPolicyPlanVersionQueryOptions(policyId, resolvedVersionId);
			const policyVersion = await queryClient.ensureQueryData(versionOptions);
			return { getTitle: policyVersion.display_name ?? 'Policy' };
		} catch {
			return { getTitle: 'Policy' };
		}
	},
	component: function PolicyDetail() {
		return <PraetorPolicyDetailPage />;
	}
});
